import React, { Component } from "react";
import { Col, Table, Row, notification } from "antd";
import { ContentWrapper, Icon } from "components";
import { DateTime } from "luxon";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { delayedDispatch, setBreadcrumb, setLoader, setTitle, updateCrumb } from "store/actions";
import { API, Endpoints } from "utils/api";
import strings from "utils/strings";
import { formatPrice, getSuccessLevelBadge, xlsxSettings } from "utils/utils";
import moment from "moment";
import { push } from "connected-react-router";
import { Area } from "@ant-design/charts";
import xlsx from "json-as-xlsx";
import { getColumnFilter } from "utils/comp-utils";

class NewPartners extends Component<any, any> {
	searchTimeout: NodeJS.Timeout | undefined;

	constructor(props: any) {
		super(props);

		this.state = {
			data: [],
			startDate: moment().add({ months: -5 }).startOf("month"),
			endDate: moment().endOf("month"),
			partner: undefined,
			partnerOptions: [],
			partnerSearch: "",
			totalEarnedPeriod: 0
		};
	}

	async componentDidMount(): Promise<void> {
		const { dispatch } = this.props;

		dispatch(setTitle(""));

		await this.getData();
		this.breadcrumb();
	}

	componentDidUpdate(): void {
		const { dispatch } = this.props;
		dispatch(updateCrumb());
	}

	async getData() {
		const { startDate, endDate, partner } = this.state;
		const { dispatch, countries } = this.props;

		dispatch(setLoader(true));

		const body = {
			startDate: startDate.toISOString(),
			endDate: endDate.toISOString(),
			type: "new-partner",
			partner
		};

		try {
			const response = await API.post({
				url: Endpoints.uriCommissions("search-by-type"),
				data: body
			});

			if (response.ok) {
				const { commissions = [], cards, commissionsChart } = response.data.results;
				let total = 0;

				const commissionsParsed = commissions.map((entry: any) => {
					total += entry.value;
					const countryCallingCode = entry?.origin?.phone?.substring(0, 4).replace("+", "");
					let country;

					if (countryCallingCode) {
						const countryObj = countries.find((elem: any) => elem.callingCodes.includes(countryCallingCode));
						country = {
							name: countryObj?.name,
							alpha2Code: countryObj?.alpha2Code
						};
					}

					return { ...entry, origin: { ...entry.origin, country } };
				});

				this.setState({
					data: commissionsParsed,
					cards,
					commissionsChart,
					totalEarnedPeriod: total
				});
			} else {
				notification.error({
					message: strings.commissions.sales,
					description: response?.data?.message || strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: strings.serverErrors.title,
				description: err as string,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	handleSearch(partner: any) {
		this.setState({ partnerSearch: partner });

		if (partner.length < 3) return;

		if (this.searchTimeout) clearTimeout(this.searchTimeout);

		this.searchTimeout = setTimeout(async () => {
			this.getUsers();
		}, 500);
	}

	async getUsers() {
		const { partnerSearch } = this.state;

		try {
			const body = { search: partnerSearch, page: 0, perPage: 10 };

			const response = await API.post({
				url: Endpoints.uriPartners("search"),
				data: body
			});

			if (response.ok) {
				const { users = [] } = response.data.results || {};
				this.setState({ partnerOptions: users });
			} else {
				notification.error({
					message: strings.commissions.sales,
					description: response?.data?.message || strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: strings.serverErrors.title,
				description: err as string,
				placement: "bottomRight",
				duration: 5
			});
		}
	}

	breadcrumb() {
		delayedDispatch(
			setBreadcrumb(() => {
				const { startDate, endDate, partnerOptions } = this.state;

				return {
					locations: [
						{
							text: strings.commissions.newPartners,
							icon: "organization"
						}
					],
					actions: [
						{
							type: "select",
							text: strings.fields.partner,
							placeholder: strings.generic.searchByEmailOrName,
							options: partnerOptions.map((partner: any) => ({
								value: partner._id,
								text: `${partner.name} (${partner.email})`
							})),
							onChange: (partner: any) => {
								this.setState({ partner }, () => this.getData());
							},
							value: this.state.partner,
							showSearch: true,
							onSearch: (value: string) => this.handleSearch(value),
							separator: "right",
							style: { width: 300, height: 40 },
							allowClear: true
						},
						{
							type: "datePicker",
							text: strings.dashboard.period,
							dates: [startDate, endDate],
							monthly: true,
							onChange: this.handleSelect,
							className: "fixedPicker"
						}
					]
				};
			})
		);
	}

	handleSelect = (dates: any) => {
		const newDates = [];
		if (dates && dates[0]) {
			const start = moment(dates[0]);
			newDates.push(start.startOf("month"));
		}

		if (dates && dates[1]) {
			const end = moment(dates[1]);
			newDates.push(end.endOf("month"));
		}

		if (newDates[0] && newDates[1] && newDates[0].isSame(newDates[1], "month")) {
			newDates[0] = moment(newDates[0]).startOf("month");
			newDates[1] = moment(newDates[1]).endOf("month");
		}

		this.setState(
			{
				startDate: newDates[0],
				endDate: newDates[1]
			},
			() => this.getData()
		);
	};

	exportData = () => {
		const { data: exportData = [], partner, partnerOptions } = this.state;

		const columns = [
			{ label: "Data", value: (row: any) => DateTime.fromISO(row.date).toFormat("dd/MM/yyyy HH:mm") },
			{ label: "Número de Encomenda", value: (row: any) => (row.order ? `#${row.order.orderNumber}` : "-") },
			{ label: "Novo Parceiro", value: (row: any) => `${row.origin.name} (#${row.origin.userCode})` },
			{ label: "Parceiro", value: (row: any) => `${row.partner.name} (#${row.partner.userCode})` },
			{ label: "Nível Adquirido", value: (row: any) => getSuccessLevelBadge(row?.order?.products?.[0]?.kitLevel) || "-" },
			{ label: "Pontos", value: (row: any) => row.points },
			{ label: "Valor", value: (row: any) => formatPrice(row.value) },
			{
				label: "Data de Expiração",
				value: (row: any) => (row.expirationDate ? DateTime.fromISO(row.expirationDate).toFormat("dd/MM/yyyy HH:mm") : "-")
			}
		];

		const data = [
			{
				sheet: "Bónus de Novos Parceiros",
				columns,
				content: exportData
			}
		];

		let name = "Bónus de Novos Parceiros";
		if (partner) {
			const partnerData = partnerOptions.find((elem: any) => elem._id === partner);
			if (partnerData) name = `Bónus de Novos Parceiros ${partnerData.name}`;
		}

		xlsx(data, xlsxSettings(name));
	};

	renderMovements() {
		const { data = [], totalEarnedPeriod } = this.state;
		const { dispatch, countries } = this.props;

		return (
			<ContentWrapper>
				<div className="ScreenHeader --multi">
					<div className="ScreenHeaderLeft">
						<Icon name="user" />
						<h2>{strings.commissions.newPartners}</h2>
					</div>
					<div className="ScreenHeaderRight">
						<p>{strings.formatString(strings.wallet.earnedAndRegisteredPeriod, formatPrice(totalEarnedPeriod), data.length)}</p>
						<button className="EthicAddButton" onClick={this.exportData}>
							<Icon name="print" />
						</button>
					</div>
				</div>
				<Table
					style={{ marginTop: 20 }}
					scroll={{ x: "max-content" }}
					columns={[
						{
							title: strings.fields.date,
							dataIndex: "date",
							key: "date",
							render: (date: string) => DateTime.fromISO(date).toFormat("dd/MM/yyyy HH:mm"),
							defaultSortOrder: "descend",
							sorter: (a, b) => DateTime.fromISO(a.date).toMillis() - DateTime.fromISO(b.date).toMillis(),
							width: 150
						},
						{
							title: strings.orders.orderNumber,
							dataIndex: "order",
							key: "order",
							render: (order: { orderNumber: number; products: Array<{ kitLevel: string }> }) => {
								if (order) {
									return (
										<a
											href={`/orders/${order?.orderNumber}`}
											onClick={(e) => {
												e.preventDefault();
												dispatch(push(`/orders/${order?.orderNumber}`));
											}}
										>
											#{order?.orderNumber}
										</a>
									);
								}

								return "-";
							},
							sorter: (a, b) => DateTime.fromISO(a._created).toMillis() - DateTime.fromISO(b._created).toMillis(),
							width: 150,
							...getColumnFilter("order.orderNumber")
						},
						{
							title: strings.fields.country,
							dataIndex: "origin",
							key: "originCountry",
							sorter: (a: any, b: any) => {
								return a?.country?.name.localeCompare(b?.country?.name);
							},
							render: (origin: any) => {
								return (
									<span className="TableRowAligned">
										<div className="react-tel-input">
											<div className="selected-flag">
												<span className={`flag ${origin?.country?.alpha2Code?.toLowerCase()}`} />
											</div>
										</div>
										{origin.country?.name}
									</span>
								);
							},
							...getColumnFilter(["origin.country.name", "origin.country.alpha2Code"]),
							width: 150
						},
						{
							title: strings.users.newPartner,
							dataIndex: "origin",
							key: "origin",
							render: (origin: any) => {
								if (!origin) return "-";

								return (
									<span className="TableRowAligned">
										{origin.name}
										{Boolean(origin?.userCode) ? ` (#${origin.userCode})` : ""}
									</span>
								);
							},
							sorter: (a, b) => a.origin.name?.localeCompare(b.origin.name),
							...getColumnFilter(["origin.name", "origin.userCode", "origin.email"])
						},
						{
							title: strings.fields.partner,
							dataIndex: "partner",
							key: "partner",
							render: (partner: any) => {
								if (!partner) return "-";
								const countryCallingCode = partner?.phone?.substring(0, 4).replace("+", "");
								let country;

								if (countryCallingCode) {
									country = countries
										.find((elem: any) => elem.callingCodes.includes(countryCallingCode))
										?.alpha2Code?.toLowerCase();
								}

								return (
									<span className="TableRowAligned">
										{country ? (
											<div className="react-tel-input">
												<div className="selected-flag">
													<span className={`flag ${country}`} />
												</div>
											</div>
										) : null}
										{partner.name}
										{Boolean(partner?.userCode) ? ` (#${partner.userCode})` : ""}
									</span>
								);
							},
							sorter: (a, b) => a.partner.name?.localeCompare(b.partner.name)
						},
						{
							title: strings.settings.successLevel,
							dataIndex: "partner",
							key: "partnerSuccessLevel",
							render: (partner) => getSuccessLevelBadge(partner.partner.successLevel) || "-"
						},
						{
							title: strings.users.adquiredLevel,
							dataIndex: "order",
							key: "adquired-level",
							render: (order: { orderNumber: number; products: Array<{ kitLevel: string }> }) =>
								getSuccessLevelBadge(order?.products?.[0]?.kitLevel) || "-",
							sorter: (a, b) => a.order?.products?.[0]?.kitLevel?.localeCompare(b.order?.products?.[0]?.kitLevel),
							width: 150,
							...getColumnFilter("order.products[0].kitLevel")
						},
						{
							title: strings.products.points,
							dataIndex: "points",
							key: "points",
							render: (value: number) => value,
							sorter: (a, b) => a.value - b.value,
							width: 90
						},
						{
							title: strings.commissions.value,
							dataIndex: "value",
							key: "value",
							render: (value: number) => formatPrice(value),
							sorter: (a, b) => a.value - b.value,
							width: 100
						},
						{
							title: strings.generic.expirationDate,
							dataIndex: "expirationDate",
							key: "expirationDate",
							render: (value: string) => (value ? DateTime.fromISO(value).toFormat("dd/MM/yyyy HH:mm") : "-"),
							align: "center",
							sorter: (a, b) => DateTime.fromISO(a._created).toMillis() - DateTime.fromISO(b._created).toMillis(),
							width: 150
						}
					]}
					dataSource={data}
					rowKey={(record) => record._id}
				/>
			</ContentWrapper>
		);
	}

	renderChart() {
		const { commissionsChart = [] } = this.state;

		const config = {
			data: commissionsChart,
			xField: "month",
			yField: "total",
			appendPadding: 10,
			smooth: true,
			animation: {
				appear: {
					animation: "path-in",
					duration: 0
				}
			},
			meta: {
				total: {
					alias: strings.users.newPartners
				}
			},
			tooltip: {
				formatter: (datum: any) => {
					return { name: strings.users.newPartners, value: datum.total };
				}
			}
		};

		return (
			<ContentWrapper extraStyle={{ marginBottom: 0 }}>
				<div className="ScreenHeader">
					<div className="ScreenHeaderLeft">
						<Icon name="diagram" />
						<h2>{strings.users.newPartners}</h2>
					</div>
				</div>
				<Area style={{ marginTop: 20 }} {...config} />
			</ContentWrapper>
		);
	}

	render() {
		const { cards, data, partner } = this.state;
		const { available = 0, spent = 0, expired = 0, total = 0 } = cards || {};

		const CARDS = [
			{
				icon: "wallet",
				value: total,
				description: partner ? strings.commissions.earnedFromNewPartners : strings.commissions.withThisBonus,
				type: "currency"
			},
			{
				icon: "wallet",
				value: available,
				description: strings.wallet.available,
				type: "currency"
			},
			{
				icon: "wallet",
				value: spent,
				description: strings.wallet.spent,
				type: "currency"
			},
			{
				icon: "wallet",
				value: expired,
				description: strings.wallet.expired,
				type: "currency"
			}
		];

		return (
			<div className="CommissionPartners">
				<Helmet>
					<title>{strings.commissions.newPartners}</title>
					<meta name="description" content="Description of commission of new partners" />
				</Helmet>
				<div className="IconCards">
					<Row gutter={[20, 20]}>
						{CARDS.map((card, index) => (
							<Col xs={24} md={partner ? 6 : 8} key={index}>
								<div className="IconCardOuterContainer">
									<div className="IconCard">
										<div className="IconCardHeader">
											<div className="IconCardHeaderMask" />
											<div className="IconCardType">
												<Icon name={card.icon} />
											</div>
										</div>
										<div className="IconCardBody">
											<span className="IconCardValue">
												{card.type === "currency" ? formatPrice(card.value) : card.value}
											</span>
											<h3 className="IconCardDescription">{card.description}</h3>
										</div>
									</div>
								</div>
							</Col>
						))}
						<Col xs={18}>{this.renderChart()}</Col>
						<Col xs={6}>
							<div className="IconCardOuterContainer">
								<div className="IconCard">
									<div className="IconCardHeader">
										<div className="IconCardHeaderMask" />
										<div className="IconCardType">
											<Icon name="user" />
										</div>
									</div>
									<div className="IconCardBody">
										<span className="IconCardValue">{data?.length || 0}</span>
										<h3 className="IconCardDescription">{strings.users.totalNewPartners}</h3>
									</div>
									<div className="IconCardBody">
										<span className="IconCardValue">{cards?.partnersWorkingForBinary || 0}</span>
										<h3 className="IconCardDescription">{strings.users.workingBinaryPartners}</h3>
									</div>
									<div className="IconCardBody">
										<span className="IconCardValue">
											{(cards?.totalPartners || 0) - (cards?.partnersWorkingForBinary || 0) || 0}
										</span>
										<h3 className="IconCardDescription">{strings.users.totalPartners}</h3>
									</div>
								</div>
							</div>
						</Col>
						<Col xs={24}>{this.renderMovements()}</Col>
					</Row>
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state: any) => ({
	language: state.language,
	countries: state.countries
});

export default connect(mapStateToProps)(NewPartners);
